// File: src/pages/routingFigures.js
// Concept figures for the "Learning to Route" page. Each figure is drawn for
// one message; none reproduces a slide. Inline SVG, responsive, no library.

import React from "react";
import { dist, tourCost, tourEdges, allOptima, LS_CITIES, SL_PROBLEMS } from "./routingLabs";

const INK = "#242424";
const MUTED = "#5a5a5a";
const RULE = "#d8d8d8";
const PANEL = "#f6f6f4";
const YELLOW = "#fbf0a6";
const BLUE = "#bcd0f5";
const TEAL = "#a8e0dc";
const RED = "#f5b3b0";
const GREEN = "#c9e3a4";
const LILAC = "#ead3f0";
const HIGH = "#00665e";
const LOW = "#b75247";
const FONT = "ui-sans-serif, system-ui, sans-serif";
const MONO = "ui-monospace, Menlo, Consolas, monospace";

/* ----------------------------------------------------------------------- */
/* Primitives                                                               */
/* ----------------------------------------------------------------------- */

function Defs() {
  return (
    <defs>
      <marker id="rlp-ah" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
        <path d="M0,0 L8,4 L0,8 z" fill={INK} />
      </marker>
      <marker id="rlp-ah-muted" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="userSpaceOnUse">
        <path d="M0,0 L8,4 L0,8 z" fill={MUTED} />
      </marker>
    </defs>
  );
}

function Fig({ viewBox, caption, minWidth, children, label }) {
  return (
    <figure className="rlp-figure">
      <div className="rlp-figure-scroll">
        <svg viewBox={viewBox} role="img" aria-label={label || caption} style={{ minWidth: minWidth || 640 }} fontFamily={FONT}>
          <Defs />
          {children}
        </svg>
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function Box({ x, y, w, h, fill = YELLOW, lines = [], small, mono, stroke }) {
  const lineH = 17;
  const total = lines.length * lineH + (small ? 14 : 0);
  const y0 = y + h / 2 - total / 2 + 12;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} fill={fill} stroke={stroke || "rgba(0,0,0,0.18)"} rx="1" />
      {lines.map((t, i) => (
        <text key={i} x={x + w / 2} y={y0 + i * lineH} textAnchor="middle" fontSize="13.5" fill={INK} fontFamily={mono ? MONO : undefined}>
          {t}
        </text>
      ))}
      {small && (
        <text x={x + w / 2} y={y0 + lines.length * lineH} textAnchor="middle" fontSize="11" fill={MUTED}>
          {small}
        </text>
      )}
    </g>
  );
}

function Arrow({ d, dashed, muted }) {
  return (
    <path
      d={d}
      fill="none"
      stroke={muted ? MUTED : INK}
      strokeWidth="1.6"
      strokeDasharray={dashed ? "5 4" : undefined}
      markerEnd={muted ? "url(#rlp-ah-muted)" : "url(#rlp-ah)"}
    />
  );
}

function Note({ x, y, text, anchor = "start", size = 12, color = MUTED, bold, mono }) {
  const lines = Array.isArray(text) ? text : [text];
  return (
    <text x={x} y={y} textAnchor={anchor} fontSize={size} fill={color} fontWeight={bold ? "bold" : "normal"} fontFamily={mono ? MONO : undefined}>
      {lines.map((l, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : size * 1.3}>
          {l}
        </tspan>
      ))}
    </text>
  );
}

function Title({ x, y, text }) {
  return (
    <text x={x} y={y} fontSize="12" fill={MUTED} fontWeight="bold" letterSpacing="0.08em" style={{ textTransform: "uppercase" }}>
      {text}
    </text>
  );
}

// Draw a set of cities (with optional tour) scaled into a box.
function Cities({ x, y, w, h, cities, tour, highlight, highlightColor, labels = true, r = 6, stroke = INK, dotFill = BLUE }) {
  const xs = cities.map((c) => c.x);
  const ys = cities.map((c) => c.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const pad = r + 4;
  const sx = (w - 2 * pad) / Math.max(1, maxX - minX);
  const sy = (h - 2 * pad) / Math.max(1, maxY - minY);
  const s = Math.min(sx, sy);
  const ox = x + pad + ((w - 2 * pad) - (maxX - minX) * s) / 2;
  const oy = y + pad + ((h - 2 * pad) - (maxY - minY) * s) / 2;
  const P = (c) => ({ x: ox + (c.x - minX) * s, y: oy + (c.y - minY) * s });
  const edges = [];
  if (tour) {
    for (let i = 0; i < tour.length; i++) {
      const a = tour[i];
      const b = tour[(i + 1) % tour.length];
      const key = a < b ? `${a}-${b}` : `${b}-${a}`;
      const pa = P(cities[a]);
      const pb = P(cities[b]);
      const hi = highlight && highlight.has(key);
      edges.push(<line key={key} x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={hi ? highlightColor || LOW : stroke} strokeWidth={hi ? 3 : 1.5} />);
    }
  }
  return (
    <g>
      {edges}
      {cities.map((c, i) => {
        const p = P(c);
        return (
          <g key={c.id || i}>
            <circle cx={p.x} cy={p.y} r={r} fill={i === 0 ? INK : dotFill} stroke={INK} strokeWidth="1" />
            {labels && c.id && (
              <text x={p.x} y={p.y + r * 0.6} textAnchor="middle" fontSize={r * 1.5} fill={i === 0 ? "#fff" : INK} fontFamily={MONO}>
                {c.id}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

/* ----------------------------------------------------------------------- */
/* 01. The problem                                                          */
/* ----------------------------------------------------------------------- */

const LS_OPT = allOptima(LS_CITIES);

export function TspFigure() {
  const five = LS_CITIES.slice(0, 5);
  const cell = 40;
  const mx = 400;
  const my = 60;
  return (
    <Fig viewBox="0 0 900 400" caption="The problem in one picture: a set of stops, the travel cost between every pair, and one closed tour that visits each stop once. The tour drawn is the shortest of the 20,160 possible tours for these nine stops.">
      <Title x={20} y={30} text="input: stops" />
      <rect x={20} y={40} width={320} height={250} fill={PANEL} rx="4" />
      <Cities x={20} y={40} w={320} h={250} cities={LS_CITIES} tour={LS_OPT.optima[0]} r={8} />
      <Note x={20} y={315} text={`output: the shortest closed tour, cost ${LS_OPT.best.toFixed(0)}`} size={12} color={INK} />

      <Title x={mx} y={30} text="input: cost between every pair (five of nine shown)" />
      {five.map((c, i) => (
        <g key={c.id}>
          <text x={mx + cell * (i + 1) + cell / 2} y={my + 14} textAnchor="middle" fontSize="12" fontFamily={MONO} fill={INK}>
            {c.id}
          </text>
          <text x={mx + cell / 2} y={my + cell * (i + 1) + 25} textAnchor="middle" fontSize="12" fontFamily={MONO} fill={INK}>
            {c.id}
          </text>
        </g>
      ))}
      {five.map((a, i) =>
        five.map((b, j) => (
          <g key={`${i}-${j}`}>
            <rect x={mx + cell * (j + 1)} y={my + cell * (i + 1)} width={cell} height={cell} fill={i === j ? RULE : "#fff"} stroke={RULE} />
            {i !== j && (
              <text x={mx + cell * (j + 1) + cell / 2} y={my + cell * (i + 1) + 25} textAnchor="middle" fontSize="12" fontFamily={MONO} fill={INK}>
                {Math.round(dist(a, b))}
              </text>
            )}
          </g>
        ))
      )}
      <Note
        x={mx + cell * 6 + 40}
        y={my + 90}
        text={["how many tours?", "9 stops: 20,160", "148 stops: over 250 digits", "", "nobody checks them all;", "what follows is about finding", "a good one without looking"]}
        size={12}
      />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 03. Supervised learning                                                  */
/* ----------------------------------------------------------------------- */

export function SupervisedLoopFigure() {
  const cities = SL_PROBLEMS.A.cities;
  const label = [0, 1, 2, 3, 4];
  const actual = [0, 1, 3, 2, 4];
  const le = tourEdges(label);
  const ae = tourEdges(actual);
  const missing = new Set([...le].filter((e) => !ae.has(e)));
  const extra = new Set([...ae].filter((e) => !le.has(e)));
  return (
    <Fig viewBox="0 0 920 375" caption="Supervised learning is copying. A slow exact solver produces the expected output for thousands of small problems; the model is corrected toward those labels. The loss counts how the model's tour differs from the label, not how long it is.">
      <Box x={20} y={110} w={160} h={100} fill={YELLOW} lines={[]} />
      <Note x={100} y={130} anchor="middle" text="Input" size={13.5} color={INK} />
      <Note x={100} y={144} anchor="middle" text="stops and costs" size={11} />
      <Cities x={30} y={148} w={140} h={58} cities={cities} r={4} labels={false} />
      <Arrow d="M180,160 L225,160" />
      <Box x={225} y={125} w={120} h={70} fill={YELLOW} lines={["Model"]} small="parameters" />
      <Arrow d="M345,160 L395,105" />
      <Arrow d="M345,160 L395,225" />

      <rect x={395} y={40} width={205} height={120} fill="#fff" stroke={INK} />
      <Note x={405} y={58} text="actual output" size={12} color={INK} bold />
      <Cities x={400} y={62} w={100} h={95} cities={cities} tour={actual} highlight={extra} highlightColor={HIGH} r={5} />
      <Note x={505} y={95} text={["a-b-d-c-e", `cost ${tourCost(cities, actual).toFixed(0)}`]} size={11} mono />

      <rect x={395} y={190} width={205} height={120} fill="#fff" stroke={INK} />
      <Note x={405} y={208} text="expected output (label)" size={12} color={INK} bold />
      <Cities x={400} y={212} w={100} h={95} cities={cities} tour={label} highlight={missing} highlightColor={LOW} r={5} />
      <Note x={505} y={245} text={["a-b-c-d-e", `cost ${tourCost(cities, label).toFixed(0)}`, "exact solver"]} size={11} mono />

      <Arrow d="M600,100 L645,150" />
      <Arrow d="M600,250 L645,200" />
      <Box x={640} y={135} w={150} h={80} fill={RED} lines={["Loss"]} small={`${missing.size} label edges missing`} />
      <Note x={700} y={240} text={["red: in the label, not produced", "green: produced, not in the label"]} size={11} />

      <Arrow d="M660,215 C660,345 285,345 285,197" dashed />
      <Note x={470} y={368} text="update the parameters so the loss shrinks next time" anchor="middle" size={12} color={INK} />
    </Fig>
  );
}

// Project the five-stop problem into a square panel with the same scaling
// that <Cities> uses for r = 6, so custom edges line up with its dots.
function projFive(px, py, w) {
  const cities = SL_PROBLEMS.A.cities;
  const s = (w - 20) / 160;
  return cities.map((c) => ({ x: px + 10 + (c.x - 40) * s, y: py + 10 + (c.y - 40) * s }));
}

// How often each pair appears across the four equally good tours of the
// five-stop problem: hull edges 3 of 4, spokes to the centre 2 of 4,
// diagonals never.
const FIVE_HEAT = [
  [0, 1, 0.75],
  [1, 2, 0.75],
  [2, 3, 0.75],
  [3, 0, 0.75],
  [0, 4, 0.5],
  [1, 4, 0.5],
  [2, 4, 0.5],
  [3, 4, 0.5],
  [0, 2, 0],
  [1, 3, 0],
];

function FivePanel({ x, y, w, tour, highlight, highlightColor, heat, title, fill = "#fff" }) {
  const cities = SL_PROBLEMS.A.cities;
  const P = projFive(x, y, w);
  return (
    <g>
      <rect x={x} y={y} width={w} height={w} fill={fill} stroke={INK} />
      {title && (
        <text x={x + w / 2} y={y - 6} textAnchor="middle" fontSize="11" fill={MUTED}>
          {title}
        </text>
      )}
      {heat &&
        FIVE_HEAT.map(([a, b, h]) => (
          <line
            key={`${a}-${b}`}
            x1={P[a].x}
            y1={P[a].y}
            x2={P[b].x}
            y2={P[b].y}
            stroke={h === 0 ? RULE : HIGH}
            strokeWidth={h === 0 ? 1 : 1 + h * 5}
            strokeDasharray={h === 0 ? "3 3" : undefined}
            opacity={h === 0 ? 1 : 0.35 + h * 0.65}
          />
        ))}
      <Cities x={x} y={y} w={w} h={w} cities={cities} tour={tour} highlight={highlight} highlightColor={highlightColor} r={6} />
    </g>
  );
}

export function ThreeUsesFigure() {
  const cities = SL_PROBLEMS.A.cities;
  const best = [0, 1, 2, 3, 4]; // a-b-c-d-e
  const oneShot = [0, 1, 3, 2, 4]; // a-b-d-c-e
  const bestCost = tourCost(cities, best).toFixed(0);
  const oneShotCost = tourCost(cities, oneShot).toFixed(0);
  const pct = (((tourCost(cities, oneShot) - tourCost(cities, best)) / tourCost(cities, best)) * 100).toFixed(0);
  const rows = [
    { y: 70, name: ["One shot:", "the model", "writes the plan"] },
    { y: 320, name: ["Guide the search:", "the model", "advises a solver"] },
    { y: 570, name: ["Pre-train, then", "reinforcement", "learning"] },
  ];
  const C1 = 170;
  const C2 = 380;
  const C3 = 600;
  const C4 = 790;
  const PW = 140;
  const colTitle = (x, t) => (
    <text x={x} y={40} fontSize="12" fontWeight="bold" fill={MUTED} letterSpacing="0.08em" style={{ textTransform: "uppercase" }}>
      {t}
    </text>
  );
  return (
    <Fig
      viewBox="0 0 980 850"
      minWidth={760}
      caption="One instance, three ways to use the same trained model. Five stops, the costs between them, and one rule the planner must respect: stop c has to be reached before 10:00. Read each row left to right: what the model sees, what it outputs, what happens to that output, and what you end up driving."
    >
      {colTitle(C1, "the same input")}
      {colTitle(C2, "what the model outputs")}
      {colTitle(C3, "what happens next")}
      {colTitle(C4, "what you get")}
      <line x1={20} y1={50} x2={960} y2={50} stroke={RULE} />

      {rows.map((r) => (
        <g key={r.y}>
          <Note x={20} y={r.y + 60} text={r.name} size={13} color={INK} bold />
          <FivePanel x={C1} y={r.y} w={PW} />
          <Note x={C1} y={r.y + PW + 18} text={["five stops, the cost of every pair,", "and a rule: reach c before 10:00"]} size={10.5} />
          <Arrow d={`M${C1 + PW + 6},${r.y + PW / 2} L${C2 - 6},${r.y + PW / 2}`} />
        </g>
      ))}

      {/* Row 1: one shot */}
      <FivePanel x={C2} y={70} w={PW} tour={oneShot} />
      <Note x={C2} y={70 + PW + 18} text={["a complete order in one go:", `a-b-d-c-e, cost ${oneShotCost}`]} size={10.5} />
      <Arrow d={`M${C2 + PW + 6},140 L${C3 - 6},140`} />
      <Box x={C3} y={95} w={170} h={90} fill={YELLOW} lines={["Nothing.", "You drive what", "the model wrote."]} />
      <Arrow d={`M${C3 + 176},140 L${C4 - 6},140`} />
      <Box x={C4} y={70} w={170} h={140} fill={RED} lines={["Invalid plan.", "c is the 4th stop,", "reached after 10:00.", `Cost ${oneShotCost}, ${pct}% above`, `the best (${bestCost}).`]} />
      <Note x={C4} y={70 + 140 + 18} text={["the model never saw the rule;", "nothing in this row checks it"]} size={10.5} />

      {/* Row 2: guide the search */}
      <FivePanel x={C2} y={320} w={PW} heat />
      {(() => {
        const P = projFive(C2, 320, PW);
        const mid = (a, b) => ({ x: (P[a].x + P[b].x) / 2, y: (P[a].y + P[b].y) / 2 });
        const m1 = mid(0, 1);
        const m2 = mid(2, 4);
        const m3 = mid(0, 2);
        return (
          <g fontSize="9.5" fontFamily={MONO}>
            <rect x={m1.x - 14} y={m1.y - 7} width={28} height={13} fill="#fff" opacity="0.9" />
            <text x={m1.x} y={m1.y + 3} textAnchor="middle" fill={HIGH}>
              75%
            </text>
            <rect x={m2.x - 14} y={m2.y - 7} width={28} height={13} fill="#fff" opacity="0.9" />
            <text x={m2.x} y={m2.y + 3} textAnchor="middle" fill={HIGH}>
              50%
            </text>
            <rect x={m3.x - 12} y={m3.y - 18} width={24} height={13} fill="#fff" opacity="0.9" />
            <text x={m3.x} y={m3.y - 8} textAnchor="middle" fill={MUTED}>
              0%
            </text>
          </g>
        );
      })()}
      <Note x={C2} y={320 + PW + 18} text={["not an order: for every pair, how", "likely it is to be in a good tour", "(thick and dark = likely)"]} size={10.5} />
      <Arrow d={`M${C2 + PW + 6},390 L${C3 - 6},390`} />
      <Box x={C3} y={330} w={170} h={120} fill={TEAL} lines={["The loop from", "section 02 runs,", "but only tries edges", "the map rates high,", "and checks the rule", "on every change."]} />
      <Arrow d={`M${C3 + 176},390 L${C4 - 6},390`} />
      <FivePanel x={C4} y={320} w={PW} tour={best} fill={GREEN} />
      <Note x={C4} y={320 + PW + 18} text={[`a-b-c-d-e, cost ${bestCost}, the best.`, "c is the 3rd stop, before 10:00.", "Price: search time, one model call."]} size={10.5} />

      {/* Row 3: pre-train then RL */}
      <FivePanel x={C2} y={570} w={PW} tour={best} />
      <Note x={C2} y={570 + PW + 18} text={["a complete order again, but from", "a model that kept training on its", "own tours with reward = minus cost;", `here it writes a-b-c-d-e, cost ${bestCost}`]} size={10.5} />
      <Arrow d={`M${C2 + PW + 6},640 L${C3 - 6},640`} />
      <Box x={C3} y={585} w={170} h={110} fill={YELLOW} lines={["Same as one shot:", "you drive it, or", "hand it to a search", "as a starting point."]} />
      <Arrow d={`M${C3 + 176},640 L${C4 - 6},640`} />
      <Box x={C4} y={570} w={170} h={140} fill="#f5c89c" lines={["Shorter tours than", "the copy, and no", "labeller needed.", "The rule is still not", "in the model: valid only", "if a search or a mask", "checks it."]} />

      <line x1={20} y1={790} x2={960} y2={790} stroke={RULE} />
      <Note
        x={20}
        y={812}
        text={["Same input, same kind of model. What differs is whether the model's output is the plan you drive,", "or advice to a solver that does the checking. The middle row is the design that survived."]}
        size={12.5}
        color={INK}
      />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 04. Where supervised learning breaks                                     */
/* ----------------------------------------------------------------------- */

export function GeneralizationFigure() {
  const rows = [
    { name: "POMO, trained at 100 stops, tested at 100", v: 0.69 },
    { name: "LEHD, trained at 100, tested at 1,000", v: 4.91 },
    { name: "BQ-NCO, trained at 100, tested at 1,000", v: 5.88 },
    { name: "POMO, trained at 100, tested at 1,000", v: 128.9 },
  ];
  const x0 = 330;
  const wmax = 540;
  const scale = wmax / 130;
  return (
    <Fig viewBox="0 0 920 230" caption="Gap to the reference solver (LKH-3) on capacitated routing, in percent. A model that is within 1% at the size it was trained on can be more than twice as long as the reference at ten times that size. Numbers from the LEHD paper's Table 1, as read in the research report.">
      {rows.map((r, i) => {
        const y = 30 + i * 45;
        return (
          <g key={r.name}>
            <text x={x0 - 12} y={y + 18} textAnchor="end" fontSize="12.5" fill={INK}>
              {r.name}
            </text>
            <rect x={x0} y={y} width={Math.max(2, r.v * scale)} height={26} fill={r.v > 50 ? LOW : HIGH} opacity={0.85} />
            <text x={x0 + Math.max(2, r.v * scale) + 8} y={y + 18} fontSize="12.5" fill={INK} fontFamily={MONO}>
              {r.v}%
            </text>
          </g>
        );
      })}
      <line x1={x0} y1={22} x2={x0} y2={212} stroke={INK} />
    </Fig>
  );
}

function clusterPoints() {
  const centers = [
    [90, 95],
    [290, 70],
    [200, 230],
  ];
  const offs = [
    [-40, -20],
    [30, -35],
    [45, 15],
    [-10, 35],
    [-45, 20],
    [10, -5],
    [30, 40],
  ];
  return centers.map(([cx, cy], k) =>
    offs.map(([dx, dy], i) => ({ id: "", x: cx + dx * (1 + 0.1 * k), y: cy + dy * (1 - 0.08 * k) + (i % 2) * 6 }))
  );
}

export function DecomposeFigure() {
  const clusters = clusterPoints();
  const tours = clusters.map((c) => allOptima(c).optima[0]);
  const fills = [BLUE, GREEN, LILAC];
  return (
    <Fig viewBox="0 0 920 320" caption="The way out of the size problem. Train the model on small problems only. At solve time, cluster the big problem, let the small model solve each cluster, and stitch. The local structure the model learned still applies inside each piece.">
      <Title x={20} y={28} text="1. a problem far bigger than anything the model saw" />
      <rect x={20} y={40} width={380} height={260} fill={PANEL} rx="4" />
      {clusters.map((c, k) => (
        <g key={k}>
          <ellipse
            cx={20 + c.reduce((s, p) => s + p.x, 0) / c.length}
            cy={40 + c.reduce((s, p) => s + p.y, 0) / c.length}
            rx={70}
            ry={58}
            fill="none"
            stroke={MUTED}
            strokeDasharray="5 4"
          />
          {c.map((p, i) => (
            <circle key={i} cx={20 + p.x} cy={40 + p.y} r={5} fill={fills[k]} stroke={INK} />
          ))}
        </g>
      ))}
      <Note x={30} y={290} text="2. cluster it into pieces the size the model was trained on" size={12} color={INK} />

      <Arrow d="M410,170 L460,170" />
      <Box x={460} y={120} w={120} h={100} fill={YELLOW} lines={["Small model"]} small="one call per cluster" />
      <Arrow d="M580,170 L630,170" />

      <Title x={630} y={28} text="3. solve each piece, stitch the pieces" />
      <rect x={630} y={40} width={270} height={260} fill={PANEL} rx="4" />
      <g transform="translate(630,40) scale(0.71)">
        {clusters.map((c, k) => (
          <g key={k}>
            {tours[k].map((idx, i) => {
              const a = c[idx];
              const b = c[tours[k][(i + 1) % tours[k].length]];
              return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={INK} strokeWidth="2" />;
            })}
            {c.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={6} fill={fills[k]} stroke={INK} />
            ))}
          </g>
        ))}
        <path d="M135,60 L245,45" stroke={MUTED} strokeWidth="2" strokeDasharray="6 5" />
        <path d="M320,105 L245,205" stroke={MUTED} strokeWidth="2" strokeDasharray="6 5" />
        <path d="M150,225 L55,130" stroke={MUTED} strokeWidth="2" strokeDasharray="6 5" />
      </g>
      <Note x={640} y={290} text="dashed: the stitches between pieces" size={12} color={INK} />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 05. Reinforcement learning                                               */
/* ----------------------------------------------------------------------- */

export function RlLoopFigure() {
  const steps = [
    { t: "1", state: "(empty)", action: "a", reward: "0" },
    { t: "2", state: "a", action: "d", reward: "-2" },
    { t: "3", state: "a-d", action: "c", reward: "-5" },
    { t: "4", state: "a-d-c", action: "…", reward: "…" },
    { t: "5", state: "a-d-c-b-e", action: "close", reward: "+500" },
  ];
  const x0 = 470;
  const colW = 86;
  return (
    <Fig viewBox="0 0 920 330" caption="No labels. The model acts, the environment answers with a reward and a new state, and the sequence of steps until termination is one session. The value of a session is the sum of its rewards. Here the reward is minus the distance just travelled, plus a bonus for closing the tour.">
      <Box x={110} y={40} w={170} h={70} fill={GREEN} lines={["Model"]} small="the decision maker" />
      <Box x={110} y={220} w={170} h={70} fill={YELLOW} lines={["Environment"]} small="the stops, the costs, the rules" />
      <Arrow d="M250,110 L250,220" />
      <Note x={262} y={160} text={["action:", "the next stop"]} size={12} color={INK} />
      <Arrow d="M140,220 L140,110" />
      <Note x={20} y={150} text={["reward,", "new state"]} size={12} color={INK} anchor="start" />
      <Note x={195} y={315} text="one loop per decision" anchor="middle" size={12} />

      <Title x={x0} y={30} text="one session, step by step" />
      {["state", "action", "reward"].map((row, r) => (
        <text key={row} x={x0 - 10} y={70 + r * 50} textAnchor="end" fontSize="12" fill={MUTED} fontWeight="bold">
          {row}
        </text>
      ))}
      {steps.map((s, i) => {
        const x = x0 + i * colW;
        return (
          <g key={s.t}>
            <rect x={x} y={50} width={colW - 6} height={30} fill={BLUE} />
            <text x={x + (colW - 6) / 2} y={70} textAnchor="middle" fontSize="12" fontFamily={MONO} fill={INK}>
              {s.state}
            </text>
            <rect x={x} y={100} width={colW - 6} height={30} fill={GREEN} />
            <text x={x + (colW - 6) / 2} y={120} textAnchor="middle" fontSize="12" fontFamily={MONO} fill={INK}>
              {s.action}
            </text>
            <rect x={x} y={150} width={colW - 6} height={30} fill={YELLOW} />
            <text x={x + (colW - 6) / 2} y={170} textAnchor="middle" fontSize="12" fontFamily={MONO} fill={INK}>
              {s.reward}
            </text>
            {i < steps.length - 1 && <Arrow d={`M${x + colW - 6},65 L${x + colW},65`} muted />}
          </g>
        );
      })}
      <path d={`M${x0},200 L${x0 + steps.length * colW - 6},200`} stroke={INK} />
      <Note x={x0 + (steps.length * colW - 6) / 2} y={222} anchor="middle" text="value of the session = sum of rewards = 500 minus the tour length" size={12.5} color={INK} bold />
      <Note x={x0} y={262} text={["the state is what the model can see: the stops, the costs, and the partial tour so far;", "the model learns to predict, for each possible action in a state, the value that follows"]} size={12} />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 06. Exploration and exploitation                                         */
/* ----------------------------------------------------------------------- */

export function PolicyFigure() {
  const q = [1, 2, 3, 1];
  const probs = [15, 20, 50, 15];
  const acts = ["A", "B", "C", "D"];
  const panel = (px, title) => (
    <g>
      <rect x={px} y={20} width={280} height={280} fill={PANEL} rx="4" />
      <Title x={px + 12} y={42} text={title} />
    </g>
  );
  const bars = (px, vals, max, pick, color, fmt) =>
    acts.map((a, i) => {
      const h = (vals[i] / max) * 110;
      const x = px + 30 + i * 60;
      return (
        <g key={a}>
          <rect x={x} y={190 - h} width={36} height={h} fill={i === pick ? color : BLUE} />
          <text x={x + 18} y={208} textAnchor="middle" fontSize="12" fill={INK}>
            {a}
          </text>
          <text x={x + 18} y={183 - h} textAnchor="middle" fontSize="11" fill={INK} fontFamily={MONO}>
            {fmt(vals[i])}
          </text>
        </g>
      );
    });
  return (
    <Fig viewBox="0 0 920 310" caption="Three ways to decide in state I when the model's estimates are A 1, B 2, C 3, D 1. Off-policy: take the best, but sometimes try anything, and keep learning about the best. On-policy: turn the estimates into probabilities and roll the dice; the model owns the trade-off. Tree search: simulate many futures first, then commit.">
      {panel(20, "off-policy: exploit, explore a bit")}
      {bars(20, q, 3.5, 2, HIGH, (v) => v)}
      <Note x={40} y={240} text={["value estimates per action", "greedy pick: C", "with probability ε: any action at random,", "and still learn the value of the greedy choice"]} size={11.5} />

      {panel(320, "on-policy: roll the dice")}
      {bars(320, probs, 60, 2, HIGH, (v) => `${v}%`)}
      <Note x={340} y={240} text={["the model outputs a probability per action", "sample one; here the dice said D", "exploration shrinks as the probabilities", "sharpen with experience"]} size={11.5} />

      {panel(620, "tree search: simulate first")}
      <circle cx={760} cy={80} r={14} fill={YELLOW} stroke={INK} />
      <text x={760} y={84} textAnchor="middle" fontSize="12" fill={INK}>
        I
      </text>
      {acts.map((a, i) => {
        const x = 665 + i * 63;
        const best = i === 1;
        return (
          <g key={a}>
            <line x1={760} y1={94} x2={x} y2={130} stroke={best ? HIGH : MUTED} strokeWidth={best ? 2.5 : 1.2} />
            <circle cx={x} cy={140} r={12} fill={best ? GREEN : "#fff"} stroke={INK} />
            <text x={x} y={144} textAnchor="middle" fontSize="11" fill={INK}>
              {a}
            </text>
            {[0, 1, 2, 3, 4].map((k) => (
              <line key={k} x1={x} y1={152} x2={x - 16 + k * 8} y2={190} stroke={best ? HIGH : RULE} strokeWidth="1" />
            ))}
          </g>
        );
      })}
      <Note x={640} y={225} text={["from I, roll the probabilities forward", "many times per action (thin lines)", "B led to the best futures most often,", "so take B, whatever the single estimate says"]} size={11.5} />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 07. Where direct RL breaks                                               */
/* ----------------------------------------------------------------------- */

export function ActionTreeFigure() {
  const n = 9;
  const x0 = 60;
  const gap = 46;
  const kinds = ["ok", "irr", "irr", "ok", "bad", "irr", "ok", "irr", "irr"];
  return (
    <Fig viewBox="0 0 920 290" caption="Two ways a naive learner drowns. Most branches from any state are irrelevant (grey), yet a learner that knows nothing must try them. Some branches violate a constraint (red) and a mask has to remove them; follow the masked branches far enough and you can reach a state with nothing left to do.">
      <circle cx={x0 + 4 * gap} cy={40} r={16} fill={YELLOW} stroke={INK} />
      <text x={x0 + 4 * gap} y={44} textAnchor="middle" fontSize="12" fill={INK}>
        state
      </text>
      {Array.from({ length: n }).map((_, i) => {
        const x = x0 + i * gap;
        const k = kinds[i];
        const col = k === "ok" ? INK : k === "bad" ? LOW : RULE;
        return (
          <g key={i}>
            <line x1={x0 + 4 * gap} y1={56} x2={x} y2={110} stroke={col} strokeWidth={k === "ok" ? 1.8 : 1.2} />
            <circle cx={x} cy={120} r={10} fill={k === "ok" ? BLUE : k === "bad" ? RED : "#fff"} stroke={col} />
            {k === "bad" && (
              <g stroke={LOW} strokeWidth="2">
                <line x1={x - 6} y1={114} x2={x + 6} y2={126} />
                <line x1={x + 6} y1={114} x2={x - 6} y2={126} />
              </g>
            )}
            {k === "ok" &&
              [0, 1, 2].map((j) => <line key={j} x1={x} y1={130} x2={x - 14 + j * 14} y2={170} stroke={j === 1 ? INK : RULE} />)}
          </g>
        );
      })}
      <Note x={x0 - 40} y={205} text={["blue: worth trying   grey: irrelevant, but the learner does not know that yet   red: violates a constraint, masked out"]} size={11.5} />
      <Note x={x0 - 40} y={232} text={["with 148 stops the first decision has 148 branches, the second 147, and so on; the number of complete paths is the 250-digit number from section 01"]} size={11.5} />

      <rect x={640} y={30} width={260} height={150} fill={PANEL} rx="4" />
      <Title x={652} y={52} text="the dead end" />
      <circle cx={700} cy={110} r={14} fill={RED} stroke={INK} />
      <text x={700} y={114} textAnchor="middle" fontSize="11" fill={INK}>
        s
      </text>
      {[0, 1, 2].map((j) => (
        <g key={j} stroke={LOW} strokeWidth="1.6">
          <line x1={700} y1={124} x2={670 + j * 30} y2={160} />
          <line x1={664 + j * 30} y1={154} x2={676 + j * 30} y2={166} />
          <line x1={676 + j * 30} y1={154} x2={664 + j * 30} y2={166} />
        </g>
      ))}
      <Note x={735} y={100} text={["a state where every", "remaining action breaks", "a time window: the", "mask leaves nothing"]} size={11.5} />
    </Fig>
  );
}

export function RevisableFigure() {
  return (
    <Fig viewBox="0 0 920 250" caption="The limitation nobody imposed on us. Reinforcement learning grew up where time only moves forward: a chess move cannot be taken back. A route plan on a screen can be revised for free until the first van leaves. Building the tour one stop at a time and never revisiting throws that freedom away.">
      <Title x={20} y={30} text="build once, never revise (direct RL construction)" />
      {Array.from({ length: 10 }).map((_, i) => (
        <g key={i}>
          <circle cx={40 + i * 40} cy={80} r={11} fill={GREEN} stroke={INK} />
          <text x={40 + i * 40} y={84} textAnchor="middle" fontSize="10" fill={INK}>
            {i + 1}
          </text>
          {i < 9 && <line x1={51 + i * 40} y1={80} x2={69 + i * 40} y2={80} stroke={INK} />}
        </g>
      ))}
      <Arrow d="M415,80 L455,80" />
      <Box x={455} y={55} w={110} h={50} fill={BLUE} lines={["1 tour"]} />
      <Note x={20} y={125} text={["10 model calls for a 10-stop tour, each one final;", "one complete tour is ever evaluated"]} size={12} />

      <Title x={20} y={175} text="search: revise freely (what every heuristic does)" />
      <Box x={20} y={190} w={110} h={50} fill={BLUE} lines={["a tour"]} />
      <path d="M130,205 C170,170 210,170 250,205" fill="none" stroke={INK} strokeWidth="1.6" markerEnd="url(#rlp-ah)" />
      <path d="M250,225 C210,260 170,260 130,225" fill="none" stroke={INK} strokeWidth="1.6" markerEnd="url(#rlp-ah)" />
      <Box x={250} y={190} w={150} h={50} fill={TEAL} lines={["a small change"]} />
      <Note x={420} y={205} text={["keep it or undo it; repeat"]} size={12} color={INK} />
      <Note x={420} y={228} text={["300 complete tours evaluated in the time the learner made 10 calls"]} size={12} />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 08. Inference cost                                                       */
/* ----------------------------------------------------------------------- */

export function InferenceCostFigure() {
  const unit = 38;
  return (
    <Fig viewBox="0 0 920 210" caption="Why a smarter picker can lose. Each iteration of the loop must pay for its decision. A network forward pass costs many times a plain cost evaluation, so the learned picker gets fewer iterations in the same budget and has to make each one count for more than it cost.">
      <Title x={20} y={30} text="one iteration, random picker" />
      <rect x={20} y={40} width={unit} height={28} fill={TEAL} stroke={INK} />
      <Note x={20 + unit + 10} y={60} text="evaluate the change (microseconds)" size={12} color={INK} />

      <Title x={20} y={110} text="one iteration, learned picker" />
      <rect x={20} y={120} width={unit * 5} height={28} fill={GREEN} stroke={INK} />
      <rect x={20 + unit * 5} y={120} width={unit} height={28} fill={TEAL} stroke={INK} />
      <Note x={20 + unit * 6 + 10} y={140} text="model call (milliseconds), then evaluate" size={12} color={INK} />

      <Note x={20} y={185} text={["same time budget: several times fewer iterations. The learner breaks even only if each chosen change is worth several random ones.", "The way out on the board and in practice: call the model rarely, where the decision is most critical."]} size={12} />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 09. Encoders                                                             */
/* ----------------------------------------------------------------------- */

const ENC_PTS = [
  [70, 60],
  [150, 40],
  [230, 70],
  [110, 130],
  [190, 140],
  [60, 200],
  [240, 190],
  [150, 230],
];

function encPanel(ox, title, mode) {
  const center = 4;
  const pts = ENC_PTS.map(([x, y]) => [x + ox, y]);
  const near = (i) =>
    pts
      .map((p, j) => ({ j, d: Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]) }))
      .filter((o) => o.j !== i)
      .sort((a, b) => a.d - b.d)
      .slice(0, 3)
      .map((o) => o.j);
  const lines = [];
  if (mode === "gnn") {
    pts.forEach((p, i) =>
      near(i).forEach((j) => {
        if (j > i) lines.push(<line key={`${i}-${j}`} x1={p[0]} y1={p[1]} x2={pts[j][0]} y2={pts[j][1]} stroke={RULE} />);
      })
    );
    near(center).forEach((j) =>
      lines.push(<line key={`c-${j}`} x1={pts[j][0]} y1={pts[j][1]} x2={pts[center][0]} y2={pts[center][1]} stroke={HIGH} strokeWidth="2.5" markerEnd="url(#rlp-ah)" />)
    );
  } else {
    pts.forEach((p, j) => {
      if (j !== center) lines.push(<line key={`a-${j}`} x1={pts[center][0]} y1={pts[center][1]} x2={p[0]} y2={p[1]} stroke={HIGH} strokeWidth="1.8" opacity="0.7" />);
    });
  }
  return (
    <g>
      <rect x={ox + 20} y={20} width={280} height={240} fill={PANEL} rx="4" />
      <Title x={ox + 32} y={42} text={title} />
      {lines}
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i === center ? 9 : 7} fill={i === center ? HIGH : BLUE} stroke={INK} />
      ))}
    </g>
  );
}

export function EncoderFigure() {
  return (
    <Fig viewBox="0 0 920 290" caption="Why the encoder is a graph network or a transformer. A set of stops has no natural order and no fixed size. A graph network lets each stop hear from its neighbours; a transformer lets each stop look at every other. Either way the same stops in any order, at any count, produce the same encoding.">
      {encPanel(0, "graph network: neighbours", "gnn")}
      {encPanel(310, "transformer: every stop", "attn")}
      <Note x={650} y={70} text={["what the encoder must be:", "", "indifferent to the order the", "stops are listed in", "", "usable for 20 stops and for 2,000", "", "aware of geometry: which stops", "are near, which are far"]} size={12.5} />
    </Fig>
  );
}

/* ----------------------------------------------------------------------- */
/* 10. Predict, Plan, Execute, Learn                                        */
/* ----------------------------------------------------------------------- */

export function PipelineFigure() {
  const stages = [
    { name: "Predict", fill: TEAL, small: ["service time, parking,", "failed attempts,", "travel-time matrix"] },
    { name: "Plan", fill: YELLOW, small: ["the loop from", "section 02, with", "learned inputs"] },
    { name: "Execute", fill: BLUE, small: ["dispatch, the", "driver's actual day,", "deviations"] },
    { name: "Learn", fill: LILAC, small: ["executed routes", "back into the", "predictions and plan"] },
  ];
  return (
    <Fig viewBox="0 0 920 250" caption="Where the learning actually goes in a routing system. This course lives in the Plan box. The 2026 evidence says that box is saturated: the wins are in learned inputs to it, in what to dispatch around it, and in a Learn stage that feeds executed reality back.">
      {stages.map((s, i) => {
        const x = 40 + i * 215;
        return (
          <g key={s.name}>
            <rect x={x} y={40} width={170} height={110} fill={s.fill} stroke="rgba(0,0,0,0.18)" />
            <text x={x + 85} y={70} textAnchor="middle" fontSize="16" fontWeight="bold" fill={INK}>
              {s.name}
            </text>
            {s.small.map((t, k) => (
              <text key={k} x={x + 85} y={95 + k * 15} textAnchor="middle" fontSize="11.5" fill={MUTED}>
                {t}
              </text>
            ))}
            {i < 3 && <Arrow d={`M${x + 170},95 L${x + 215},95`} />}
          </g>
        );
      })}
      <Arrow d="M770,150 C770,215 125,215 125,152" dashed />
      <Note x={450} y={232} anchor="middle" text="what happened today changes tomorrow's inputs" size={12} color={INK} />
      <path d="M255,160 L255,172 L425,172 L425,160" fill="none" stroke={INK} />
      <Note x={340} y={190} anchor="middle" text="this course" size={12} color={INK} bold />
    </Fig>
  );
}
